/**
 * Error-message helpers for API responses.
 *
 * FastAPI's response-validation layer returns `detail` as an **array of
 * error dicts** (e.g., [{"loc": [...], "msg": "...", "type": "..."}]).
 * The old `new Error(data.detail)` pattern then stringifies that array
 * to the literal string "[object Object]" and users see that in a toast.
 * This helper normalises every shape we can realistically get back —
 * string, array of strings, array of pydantic error dicts, nested object,
 * or missing — into a single human-readable sentence.
 */

const _toSentence = (value) => {
  if (value == null) return '';
  if (typeof value === 'string') return value.trim();
  if (Array.isArray(value)) {
    const parts = value.map(_toSentence).filter(Boolean);
    return parts.join('; ');
  }
  if (typeof value === 'object') {
    if (typeof value.msg === 'string') return value.msg;
    if (typeof value.message === 'string') return value.message;
    if (typeof value.detail === 'string') return value.detail;
    return '';
  }
  return String(value);
};

/**
 * Extract a user-readable error message from a parsed JSON response body
 * or an Error thrown by `fetch`/`res.json`.
 *
 * @param {*} source     Response JSON body OR Error object
 * @param {string} fallback  Shown when nothing usable was found
 */
export const extractErrorMessage = (source, fallback = 'Something went wrong') => {
  if (!source) return fallback;
  // Error instance → use its message directly if sensible
  if (source instanceof Error) {
    const msg = source.message || '';
    if (!msg || msg === '[object Object]') return fallback;
    return _humanize(msg) || fallback;
  }
  // Response JSON body
  const candidate =
    _toSentence(source.detail) ||
    _toSentence(source.message) ||
    _toSentence(source.error) ||
    _toSentence(source);
  return _humanize(candidate) || fallback;
};

// Map a few cryptic server-validation strings to messages a league
// organizer / coach can actually act on. Anything we don't recognise
// passes through untouched.
const _humanize = (msg) => {
  if (!msg) return msg;
  const low = msg.toLowerCase();
  // Pydantic's "field required" happens for file uploads when the
  // multipart `file` field is missing — usually an iOS Safari quirk
  // where the filename got stripped off the Blob mid-request.
  if (low === 'field required' || low === 'value_error.missing') {
    return 'The photo was lost on upload. Please tap the upload button and pick the photo again.';
  }
  return msg;
};
